/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef } from 'react'
import { PatternFormat } from 'react-number-format'

import Select from 'react-select'

import { COUNTRIES_LIST, ICountry } from '../../../fakeData/countriesList'

import { customStyles } from '../../customStyles/ReactSelectCustomStyle'

import { UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { IUserInterface } from '../../../models/users/user.interface'

export const generatePhoneMask = (length: number) => {
  if (length <= 4) return '#'.repeat(length)

  const groups: number[] = []

  let remaining = length

  // keep last 4 digits together (best UX)
  if (remaining > 4) {
    remaining -= 4
  }

  // split remaining into groups of 3
  while (remaining > 3) {
    groups.push(3)
    remaining -= 3
  }

  if (remaining > 0) {
    groups.unshift(remaining)
  }

  groups.push(4)

  return groups.map((n) => '#'.repeat(n)).join('-')
}

type IOnChange = (args: IOnChangeArgs) => void
type IOnChangeArgs = {
  length: number
  isValid: boolean
  phoneNumber: string
  countryCode: string
}

interface IPhoneInputProps {
  name: string
  onFocus?: any
  title?: string
  disable?: boolean
  validate?: boolean
  className?: string
  phoneNumber: string
  setValue?: UseFormSetValue<IUserInterface>
  countryCode: string
  onChange?: IOnChange
  placeholder?: string
  inputClassName?: string
  selectClassName?: string
  saved?: boolean
  register?: UseFormRegister<any>
  ref?: React.MutableRefObject<HTMLDivElement | null>
}

const PhoneInput = React.forwardRef<HTMLDivElement, IPhoneInputProps>((props, ref) => {
  const countryCode = useRef(props.countryCode)
  const phoneNumber = useRef(props.phoneNumber)
  const { setValue } = props

  useEffect(() => {
    countryCode.current = props.countryCode
    phoneNumber.current = props.phoneNumber
    transpile()
  }, [props.phoneNumber, props.countryCode])

  const selectList = useMemo(() => {
    let oldValue: ICountry | null = null
    let updatedArray

    updatedArray = COUNTRIES_LIST.reduce((acc, curr) => {
      oldValue = { ...curr }
      acc.push({
        ...curr,
        value: oldValue.phoneCode,
        phoneCode: oldValue.value,
      })
      return acc
    }, [] as ICountry[])

    return updatedArray
  }, [])

  const transpile = () => {
    handleChange()
  }

  const getSelectedValueDetails = () => {
    let selectedValueLength = COUNTRIES_LIST.find(
      (c) => c.value === countryCode.current
    )?.phoneNumberLength

    if (!selectedValueLength) selectedValueLength = COUNTRIES_LIST[0].phoneNumberLength

    return {
      isValid: selectedValueLength === phoneNumber.current?.length,
      phoneNumberLength: selectedValueLength,
    }
  }

  const getTrimmedValue = (maxLength: number) => {
    if (phoneNumber.current?.length > maxLength) return phoneNumber.current.substring(0, maxLength)
    else return phoneNumber.current
  }

  const handleChange = () => {
    let details = getSelectedValueDetails()

    if (typeof props.onChange === 'function')
      props.onChange({
        isValid: details?.isValid ?? false,
        phoneNumber: phoneNumber.current.replace(/[- ]/g, '') ?? '',
        length: details?.phoneNumberLength ?? 0,
        countryCode: countryCode.current?.toString() ?? '',
      })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    phoneNumber.current = e.target.value ?? ''
    phoneNumber.current = phoneNumber.current.replace(/[- ]/g, '')
    handleChange()
  }

  const handleFlagChange = (newValue: ICountry) => {
    countryCode.current = newValue.phoneCode
    phoneNumber.current = getTrimmedValue(newValue.phoneNumberLength)
    handleChange()
  }

  const getSelectValue = () => {
    let valueToReturn = COUNTRIES_LIST[0]
    let foundValue = COUNTRIES_LIST.find((c) => c.value === props?.countryCode)
    if (foundValue) valueToReturn = foundValue
    return {
      ...valueToReturn,
      label: valueToReturn.flag,
      value: valueToReturn.phoneCode,
    }
  }

  const inputProps = {
    name: props.name,
    value: props.phoneNumber,
    onChange: handleInputChange,
    onFocus: () => {
      if (typeof props?.onFocus === 'function') {
        props.onFocus()
      }
    },
    format: generatePhoneMask(getSelectedValueDetails().phoneNumberLength),
    mask: '_',
    onBlur: () => {
      if (setValue) setValue('userPhoneNumber', props.phoneNumber)
    },
    placeholder: 'Enter phone...',
    className: `!p-3 !border-0 outline-0 !ring-0 ${props?.inputClassName ?? ''} ${'text-SeabiscuitDark200ThemeColor'}`,
    disabled: props?.saved ? props.saved : (props?.disable ?? false),
    title: props?.title,
  }

  const getOptionLabel = (data: ICountry) => {
    return (
      <div className="flex">
        <img src={data.flag} alt={data.value} style={{ width: '20px', marginRight: '5px' }} />
        <span>{data.value}</span>
      </div>
    ) as unknown as string
  }

  return (
    <div
      className={`flex border rounded-xl p-0 h-[51px] items-center px-3 ${props.saved ? '!border-white text-gray-400' : '!border-[#D3DAEE]'} `}
      ref={ref}
    >
      <Select
        options={selectList}
        value={getSelectValue()}
        isDisabled={props?.saved ?? false}
        onChange={handleFlagChange as any}
        className={`selectWithSearch rounded-xl focus:ring-[#D3DAEE] focus:border-[#D3DAEE] w-[90px] `}
        styles={
          {
            ...customStyles,
            valueContainer: (provided: any) => ({
              ...provided,
              textAlign: 'left',
              justifyContent: 'start',
              fontSize: '14px',
              padding: 0,
            }),
            singleValue: (provided: any, state: any) => ({
              ...(customStyles?.singleValue && customStyles?.singleValue(provided, state)),
              fontSize: '14px !important',
              color: state.isDisabled ? '#122b46' : provided.color,
              opacity: state.isDisabled ? '1' : provided.opacity,
              ...((props?.saved || props?.disable) && {
                color: 'none',
              }),
            }),
            control: (styles: any, state: any) => ({
              ...customStyles['control']?.(styles, state),
              padding: 0,
            }),
            input: (styles: any, state: any) => ({
              ...customStyles['input']?.(styles, state),
              width: 'fit-content',
            }),
            dropdownIndicator: (styles: any, state: any) => ({
              ...customStyles['dropdownIndicator']?.(styles, state),
              padding: 0,
              display: state.isDisabled ? 'none' : '',
            }),
          } as any
        }
        getOptionLabel={getOptionLabel}
        isClearable={false}
        isSearchable={true}
      />

      <PatternFormat {...inputProps} />
    </div>
  )
})

export default PhoneInput
